import { useState, useEffect } from "react";
import { getFeedbackvideos, deleteFeedback } from "src/redux/api/_hr.api";
import { MapPin, Trash2, Play } from "lucide-react";
import { toast } from "react-toastify";
import { FallingLines } from "react-loader-spinner";

interface UploadedFeedbackVideo {
    id: string,
    location: string,
    thumbnail_key: string,
    url: string,
}

interface DeleteFeedbackProps {
    refreshTrigger?: number; // Add optional prop to trigger refresh
}

const CDN_URL = "https://d3ir3etrl1n0sj.cloudfront.net/";

function DeleteFeedback({ refreshTrigger }: DeleteFeedbackProps) {
    const [uploadedFeedbackVideo, setUploadedFeedbackVideo] = useState<UploadedFeedbackVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(false);

    const fetchFeedbackVideo = async () => {
        try {
            setLoading(true);
            const res = await getFeedbackvideos(1, 1000);
            const reversed = [...res].reverse();
            // Reverse the array so newest uploads appear first
            setUploadedFeedbackVideo(reversed);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchFeedbackVideo();
    }, []);

    // Add useEffect to refetch when refreshTrigger changes
    useEffect(() => {
        if (refreshTrigger !== undefined && refreshTrigger > 0) {
            fetchFeedbackVideo();
        }
    }, [refreshTrigger]);

    const handleDeleteFeedback = async(id: string) => { 
        try {
            setStatus(true);
            await deleteFeedback(id);
            toast.success("Feedback video deleted successfully");
            setStatus(false);
            fetchFeedbackVideo(); // Refresh the list
        } catch (error) {
            console.error("Error deleting feedback video:", error);
            toast.error("Deletion failed");
            setStatus(false)
        }
    }

    if(loading) {
        return (
            <div className="p-6 mx-auto">
                <div className="text-center text-gray-600">Loading Feedback videos</div>
            </div>
        )
    }
    
    if (uploadedFeedbackVideo.length === 0) {
        return (
            <div className="p-6 mx-auto">
                <div className="text-center text-gray-600">No Uploaded feedback videos</div>
            </div>
        )
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-8">Uploaded Feedback Videos</h1>
            
            <div className="mb-4 text-sm text-gray-600">
                Total Videos: {uploadedFeedbackVideo.length}
            </div>

            {/* Videos Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {uploadedFeedbackVideo.map((video) => (
                    <div
                        key={video.id}
                        className="relative group overflow-hidden rounded-xl border border-gray-200 shadow hover:shadow-xl transition-all duration-300"
                    >
                        {/* Thumbnail with play button overlay */}
                        <div className="relative">
                            <img
                                src={CDN_URL + video.thumbnail_key}
                                alt={video.location}
                                className="w-full h-48 object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <div className="bg-white/80 rounded-full p-2">
                                    <Play className="text-blue-600" size={20} />
                                </div>
                            </div>
                        </div>

                        {/* Video Details */}
                        <div className="p-4 bg-white">
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                                <MapPin size={14} />
                                <span>{video.location}</span>
                            </div>
                        </div>

                        {/* Delete button overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                            <button
                                onClick={() => handleDeleteFeedback(video.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transform hover:scale-110"
                                aria-label="Delete feedback video"
                            >
                                {status ? <FallingLines width="25" color="white"/> :
                                <Trash2 size={20} />}
                            </button>
                        </div>

                        {/* Video URL at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <p className="text-white text-xs truncate">{video.url}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default DeleteFeedback